import React from 'react';
import { Calendar, CheckSquare, Target } from 'lucide-react';
import { useTranslations } from '../context/TranslationContext';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const SolutionsSection: React.FC = () => {
  const { t } = useTranslations();

  const solutionsData = [
    {
      icon: <Calendar size={64} />,
      title: t("up_to_date"),
      description: t("subtitle_up_to_date"),
      bgColor: "bg-transparent",
      iconColor: "text-mystic-violet",
    },
    {
      icon: <CheckSquare size={64} />,
      title: t("zero_hallucination"),
      description: t("subtitle_zero_hallucination"),
      bgColor: "bg-transparent",
      iconColor: "text-cosmic-teal",
    },
    {
      icon: <Target size={64} />,
      title: t("hundred_percent_relevance"),
      description: t("subtitle_hundred_percent_relevance"),
      bgColor: "bg-transparent",
      iconColor: "text-nebula-pink",
    },
  ];

  return (
    // Section padding: pt-16 md:pt-24 controls TOP padding, pb-8 md:pb-12 controls BOTTOM padding
    // Reduce pb-8 md:pb-12 to pb-4 md:pb-6 for less space, or increase to pb-12 md:pb-16 for more
    <section id="solutions" className="border-t border-white/10 py-16 md:py-24 bg-void-blue">
      {/* Shared site container keeps this section aligned with product pages. */}
      <div className="mx-auto w-full max-w-7xl px-6 md:px-12">

        <div>
          <div className="mb-10 max-w-3xl">
            <h2 className="font-display text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-white leading-tight mb-4 md:mb-6">
              {t("why_choose_us")}
            </h2>
            <p className="text-base md:text-lg text-gray-300 leading-relaxed">
              {t("why_choose_us_subtitle")}
            </p>
          </div>

          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {solutionsData.map((solution, index) => {
                // Create a separate animation hook for each card
                const AnimatedCard = () => {
                  const { elementRef, isVisible } = useScrollAnimation({ threshold: 0.75 });

                  return (
                      <div
                        ref={elementRef}
                        key={index}
                        className={`glass-panel rounded-2xl p-6 md:p-8 flex flex-col border border-white/5
                          ${isVisible ? 'opacity-100 translate-y-0' : 'md:opacity-100 md:translate-y-0 opacity-0 translate-y-8'}
                          transition-all duration-700 ease-out`}
                      >
                        {/* Icon */}
                        <div className={`${solution.iconColor} mb-5`}>
                          {React.cloneElement(solution.icon, { size: 32 })}
                        </div>

                        {/* Text inside */}
                        <h3 className="text-xl font-bold text-white mb-3">{solution.title}</h3>
                        <p className="text-base leading-relaxed text-gray-400">{solution.description}</p>
                      </div>
                  );
                };

                return <AnimatedCard key={index} />;
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionsSection;
